// src/types/questionnaire.ts
//
// The questionnaire endpoint's contract, as `QuestionnaireBlockSerialiser`
// produces it. The backend parses the schema 2.0 template and maps its node
// types onto these names; the frontend never sees XML.

/** Question blocks, named as the serialiser names them — not as the XML does. */
export const QUESTIONNAIRE_QUESTION_TYPES = [
  'text',
  'textarea',
  'date',
  'radios',
  'checkboxes',
  'select',
  'quantity',
  'bloodPressure',
] as const;

export type QuestionnaireQuestionType = (typeof QUESTIONNAIRE_QUESTION_TYPES)[number];

export interface QuestionnaireOption {
  name: string;
  label: string;
  value: string;
}

export interface QuestionnaireQuestionBlock {
  type: QuestionnaireQuestionType;
  /** Template-derived, and what the submitted answers are keyed by. */
  name: string;
  label: string;
  required: boolean;
  /** radios, checkboxes and select only. */
  options?: QuestionnaireOption[];
}

export interface QuestionnaireBannerBlock {
  type: 'banner';
  title: string;
  text: string;
}

/**
 * The template's own `<description>`, prepended as the first block when present.
 * Note `value`, not `text` — the serialiser names it differently to a banner's body.
 */
export interface QuestionnaireDescriptionBlock {
  type: 'description';
  value: string;
}

export type QuestionnaireContentBlock = QuestionnaireBannerBlock | QuestionnaireDescriptionBlock;

export type QuestionnaireBlock = QuestionnaireQuestionBlock | QuestionnaireContentBlock;

export interface Questionnaire {
  id: string;
  title: string;
  versionNumber: string;
  canRespond: boolean;
  /** An error code when `canRespond` is false. */
  cannotRespondReason: string | null;
  /** Blocks in authored order — content interleaved with questions. */
  questionnaire: QuestionnaireBlock[];
}

export interface BloodPressureAnswer {
  systolic: string;
  diastolic: string;
}

/** Keyed by question name. Checkbox groups answer with an array, blood pressure with its parts. */
export type QuestionnaireAnswer = string | string[] | BloodPressureAnswer | null;

export type QuestionnaireAnswers = Record<string, QuestionnaireAnswer>;

export function isQuestionnaireQuestionBlock(
  block: QuestionnaireBlock,
): block is QuestionnaireQuestionBlock {
  return (QUESTIONNAIRE_QUESTION_TYPES as readonly string[]).includes(block.type);
}
